import { FC, PropsWithChildren, useCallback, useEffect, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import usePortal from 'react-useportal';
import { useClickOutside } from '../../hooks';
import { SVG } from '../svg';
import { ReactComponent as cross } from '../../assets/icons/cross.svg';
import { CrossContainer, Fade, ModalContainer } from './modal.styled';

export type ISize = 's' | 'm' | 'l' | 'xl';
export type IBackground = 'white' | 'grey';

interface Props {
  portalId?: string;
  onClose: () => void;
  size?: ISize;
  background?: IBackground;
  stableHeight?: boolean;
}

export const Modal: FC<PropsWithChildren<Props>> = observer(
  ({
    portalId = '#root',
    onClose,
    size = 'l',
    background = 'white',
    stableHeight = false,
    children,
  }) => {
    const { Portal } = usePortal({
      bindTo: document.getElementById(portalId)!,
    });
    const modalContainerRef = useRef<HTMLDivElement>(null);

    const escCloseListener = useCallback((e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    }, []);

    useEffect(() => {
      document.addEventListener('keydown', escCloseListener);

      return () => document.removeEventListener('keydown', escCloseListener);
    }, [escCloseListener]);

    useClickOutside(modalContainerRef, onClose, [
      '.custom-select-dropdown',
      '.media-previewer-wrapper',
      '#default-confirm',

      // TinyMCE main blocks
      '.tox-toolbar',
      '.tox-dialog',
      '.tox-dialog *',
      '.tox-menu',
      '.tox-menu *',
      '.tox-collection',
      '.tox-collection *',
      '.tox-listboxfield',
      '.tox-listboxfield *',
      '.tox-dialog-wrap__backdrop',
    ]);

    return (
      <Portal>
        <Fade />
        <ModalContainer
          ref={modalContainerRef}
          size={size}
          background={background}
          stableHeight={stableHeight}
        >
          <CrossContainer onClick={onClose}>
            <SVG
              src={cross}
              props={{
                color: '#ffffff',
                hoverColor: '#495466',
                cursor: 'pointer',
              }}
            />
          </CrossContainer>
          {children}
        </ModalContainer>
      </Portal>
    );
  }
);
